import { z } from 'zod';
import {
	AnimationEnum,
	CampaignType,
	PositionLayoutEnum,
	TimingTypeEnum,
	ComponentT,
} from '../types/campaignsApiResponse.d';

const cardPayload = z.object({
	actionButton: z.object({
		link: z.string(),
		style: z
			.object({
				backgroundColor: z.string().optional(),
				color: z.string().optional(),
				borderRadius: z.string().optional(),
				textAlign: z.string().optional(),
				fontSize: z.string().optional(),
				textDecoration: z.string().optional(),
				fontStyle: z.string().optional(),
				fontWeight: z.string().optional(),
			})
			.optional(),
		text: z.string(),
	}),
	image: z.object({
		src: z.string(),
	}),
	style: z
		.object({
			width: z.string().optional(),
			backgroundColor: z.string().optional(),
			height: z.string().optional(),
			background: z
				.object({
					color: z.string().optional(),
					opacity: z.number().optional(),
				})
				.optional(),
		})
		.optional(),
	bodyText: z.object({
		style: z
			.object({
				color: z.string().optional(),
				textAlign: z.string().optional(),
				fontSize: z.string().optional(),
				textDecoration: z.string().optional(),
				fontStyle: z.string().optional(),
				fontWeight: z.string().optional(),
			})
			.optional(),
		text: z.string(),
	}),
});

const bannerPayload = z.object({
	image: z.object({
		link: z.string(),
		src: z.string(),
	}),
	style: z
		.object({
			width: z.string().optional(),
			height: z.string().optional(),
			background: z
				.object({
					color: z.string().optional(),
					opacity: z.number().optional(),
				})
				.optional(),
		})
		.optional(),
});

const campaignBannerSchema = z.object({
	payload: bannerPayload,
	templateId: z.literal(1),
});

const campaignCardSchema = z.object({
	payload: cardPayload,
	templateId: z.literal(2),
});

const component: z.ZodSchema<ComponentT> = z.lazy(() =>
	z
		.object({
			type: z.string(),
			props: z
				.object({
					children: z
						.union([
							component,
							z.array(z.union([component, z.string()])),
							z.string(),
						])
						.optional()
						.nullable(),
				})
				.passthrough(),
		})
		.passthrough()
);

const campaignCustomHTMLSchema = z.object({
	payload: z.object({
		component,
		style: z
			.object({
				width: z.string().optional(),
				height: z.string().optional(),
				background: z
					.object({
						color: z.string().optional(),
						opacity: z.number().optional(),
					})
					.optional(),
			})
			.optional(),
	}),
	templateId: z.literal(0),
});

export const campaignSchema: z.ZodSchema<CampaignType> = z.lazy(() =>
	z.intersection(
		z.object({
			campaignId: z.string().nonempty(),
			contentId: z.string().nonempty(),
			options: z.object({
				animation: z.nativeEnum(AnimationEnum),
				position: z.object({
					layout: z.nativeEnum(PositionLayoutEnum),
					offsetX: z.number(),
					offsetY: z.number(),
				}),
				timing: z.object({
					type: z.nativeEnum(TimingTypeEnum),
					activate: z.number(),
				}),
				stopCondition: z
					.object({ viewTotal: z.number(), closeTotal: z.number() })
					.optional(),
				trigger: z
					.object({
						visit: z.object({ total: z.number() }).nullable(),
						displayTimer: z.number().nullable().optional(),
					})
					.optional(),
			}),
		}),
		z.union([
			campaignBannerSchema,
			campaignCardSchema,
			campaignCustomHTMLSchema,
		])
	)
);
